import React, { useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import { groupComposerTools, type ComposerTool } from './composerTools';

export interface SlashToolMenuProps {
  /** Already-filtered tools, in catalog order. */
  tools: ComposerTool[];
  /** Id of the highlighted tool (keyboard/hover), or null. */
  activeId: string | null;
  onSelect: (tool: ComposerTool) => void;
  onHover: (id: string) => void;
  className?: string;
}

/**
 * The dropdown shown when the user types "/" at the start of the composer. Pure
 * presentation — the parent (`ChatTextArea`) owns filtering, keyboard navigation,
 * and selection. Renders tools grouped by category with the active row highlighted,
 * and a footer strip describing the active tool (like the reference mock's tooltip).
 */
export const SlashToolMenu: React.FC<SlashToolMenuProps> = ({
  tools,
  activeId,
  onSelect,
  onHover,
  className,
}) => {
  const groups = groupComposerTools(tools);
  const active = tools.find((t) => t.id === activeId) ?? null;

  // Keep the keyboard-highlighted row visible when navigating past the overflow
  // fold. `block: 'nearest'` scrolls only when the row is out of view.
  const activeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  return (
    <div
      className={cn(
        'z-50 w-72 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg',
        'dark:border-gray-700 dark:bg-gray-900',
        className
      )}
      role="listbox"
      aria-label="Tools"
      // Keep focus in the editor so typing continues to filter; a mousedown here
      // must not blur the editor before onSelect fires.
      onMouseDown={(e) => e.preventDefault()}
    >
      {groups.length === 0 ? (
        <div className="px-3 py-4 text-sm text-gray-400">No matching tools</div>
      ) : (
        <div className="max-h-72 overflow-y-auto py-1">
          {groups.map((group) => (
            <div key={group.category}>
              <div className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                {group.category}
              </div>
              {group.tools.map((tool) => {
                const isActive = tool.id === activeId;
                return (
                  <button
                    key={tool.id}
                    ref={isActive ? activeRef : undefined}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    data-testid={`slash-tool-${tool.id}`}
                    onMouseEnter={() => onHover(tool.id)}
                    onClick={() => onSelect(tool)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
                      isActive
                        ? 'bg-primary-50 text-primary-800 dark:bg-primary-500/15 dark:text-primary-200'
                        : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800'
                    )}
                  >
                    <span className="truncate">{tool.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {active && (
        <div className="border-t border-gray-100 px-3 py-2 text-xs leading-snug text-gray-500 dark:border-gray-800 dark:text-gray-400">
          {active.description}
        </div>
      )}
    </div>
  );
};

export default SlashToolMenu;
